// Assign a hierarchical indent level to each direct child paragraph or note
// (aside) inside an eCFR-derived HTML container. Handles two marker styles:
//
//   USML (CFR canonical):   `(a)`  `(1)`  `(i)…(x)`  `(A)`
//   CCL (BIS "dotted"):     `a.`   `a.1.`  `a.1.a`   `z.4.b`
//
// Levels:
//   * top-level letter (a-x)     → level 0
//   * first sub-level (1, 2…)    → level 1
//   * roman / next sub-letter    → level 2
//   * uppercase letter           → level 3
//
// (i)/(v)/(x) are ambiguous — resolved via a stateful walker that tracks
// the last top-level letter (paren-style only).
//
// USML paragraph pages open with the parent letter (e.g. XI(a)). Pass its
// letter via `parentLetter` so the walker's first hit on that letter is
// classified as level 0.
export function annotateRegIndent(container, { parentLetter } = {}) {
  if (!container) return;
  const children = Array.from(container.children);
  let lastLevel = 0;
  let lastLetterOrd = parentLetter
    ? parentLetter.charCodeAt(0) - 1
    : ord("a") - 1;

  for (const node of children) {
    const tag = node.tagName;
    const text = (node.textContent || "").trim();

    if (tag === "ASIDE") {
      const noteLvl = levelFromNoteRef(text);
      if (noteLvl !== null) {
        node.dataset.lvl = String(noteLvl);
        lastLevel = noteLvl;
      } else {
        node.dataset.lvl = String(lastLevel);
      }
      continue;
    }

    if (tag !== "P") continue;

    // Strip a possible leading "*" bullet the eCFR uses on some paragraphs.
    const stripped = text.replace(/^\*\s*/, "");

    // 1) Parenthesized marker (USML / CFR canonical): (a), (1), (i), (A)
    const paren = stripped.match(/^\(([A-Za-z]+|\d+)\)/);
    if (paren) {
      const marker = paren[1];
      let level;
      if (/^\d+$/.test(marker)) {
        level = 1;
      } else if (/^[A-Z]+$/.test(marker)) {
        level = 3;
      } else if (marker.length === 1) {
        const o = marker.charCodeAt(0);
        if (o === lastLetterOrd + 1) {
          level = 0;
          lastLetterOrd = o;
        } else {
          level = 2;
        }
      } else {
        level = 2;
      }
      node.dataset.lvl = String(level);
      lastLevel = level;
      continue;
    }

    // 2) Dotted marker (CCL / BIS): a., a.1., a.1.a., z.4.b
    // Must start with a lowercase letter FOLLOWED immediately by a dot to
    // avoid false positives on ordinary sentences like "a general purpose".
    const dot = stripped.match(/^([a-z])\.([a-z0-9]+(?:\.[a-z0-9]+)*)?\.?(?=\s|$)/);
    if (dot) {
      const rest = dot[2] || "";
      const nSegs = 1 + (rest ? rest.split(".").length : 0);
      const level = Math.min(nSegs - 1, 3);
      node.dataset.lvl = String(level);
      lastLevel = level;
      continue;
    }

    // Unlabeled paragraph (section heading, continuation text). Leave
    // untouched so the existing default styling applies.
  }
}

function ord(ch) {
  return ch.charCodeAt(0);
}

// Parse a note/aside's reference to determine its indent level.
// Only recognises the explicit "Note … to paragraph (X)(Y)(Z):" phrasing
// used by USML / CFR. For CCL notes, which say "Note 1:" or "Technical
// Note:" without a target reference, return null so the walker falls back
// to inheriting the previous paragraph's level — the most reliable signal
// of which paragraph the note annotates.
// Level rule: 1 ref = 0, 2 refs = 1, 3 refs = 2, 4+ refs = 3.
function levelFromNoteRef(text) {
  const head = text.slice(0, 200);
  const m = head.match(/paragraph\s*((?:\s*\([^()]+\))+)/i);
  if (!m) return null;
  const refs = m[1].match(/\([^()]+\)/g);
  if (!refs || refs.length === 0) return null;
  return Math.min(refs.length - 1, 3);
}
